import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SectionCard as SectionCardType, SectionKey, SectionSpacing } from '../../types/editor'
import { SECTION_LABELS, SECTION_SUB_ELEMENTS } from '../../types/editor'

interface SectionCardProps {
  card: SectionCardType
  immovable?: boolean
  sliderValues: Record<string, number>
  onSliderChange: (subKey: string, value: number) => void
  onSubSizeChange: (subKey: string, size: 'small' | 'medium' | 'large') => void
  onEdit: (key: SectionKey) => void
  sectionSpacing?: SectionSpacing
  onSpacingChange?: (key: SectionKey, spacing: SectionSpacing) => void
}

const SIZE_OPTIONS: { value: 'small' | 'medium' | 'large'; label: string }[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
]

const MULTIPLIERS = { small: 0.8, medium: 1.0, large: 1.3 } as const

const SectionCardComponent: React.FC<SectionCardProps> = ({
  card,
  immovable = false,
  sliderValues,
  onSliderChange,
  onSubSizeChange,
  onEdit,
  sectionSpacing,
  onSpacingChange,
}) => {
  const subElements = SECTION_SUB_ELEMENTS[card.key] || []

  const sortableResult = useSortable({ id: card.key, disabled: immovable })
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortableResult

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={immovable ? undefined : setNodeRef}
      style={immovable ? undefined : style}
      className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        {!immovable && (
          <button
            type="button"
            className="cursor-grab select-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        )}
        <span className="flex-1 text-sm font-medium text-gray-800">
          {SECTION_LABELS[card.key] ?? card.key}
        </span>
        <button
          type="button"
          onClick={() => onEdit(card.key)}
          className="rounded px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
        >
          Edit
        </button>
      </div>

      <div className="divide-y divide-gray-50">
        {subElements.map((sub) => (
          <div key={sub.key} className="px-3 py-2 space-y-1.5">
            {subElements.length > 1 && (
              <span className="text-xs text-gray-500 font-medium">{sub.label}</span>
            )}
            <div className="flex items-center gap-2">
              {SIZE_OPTIONS.map((opt) => {
                const baseline = sub.baseline
                const rem = baseline * MULTIPLIERS[opt.value]
                const isActive = Math.abs((sliderValues[sub.key] ?? sub.baseline) - rem) < 0.02
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onSubSizeChange(sub.key, opt.value)}
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
              <input
                type="range"
                min="0.4"
                max={sub.maxRem}
                step="0.01"
                value={sliderValues[sub.key] ?? sub.baseline}
                onChange={(e) => onSliderChange(sub.key, parseFloat(e.target.value))}
                className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-xs text-gray-400 min-w-[48px] text-right">
                {(sliderValues[sub.key] ?? sub.baseline).toFixed(2)}rem
              </span>
            </div>
          </div>
        ))}
      </div>

      {onSpacingChange && card.key !== 'title' && card.key !== 'subtitle' && (
        <div className="px-3 py-2 border-t border-gray-100 space-y-1.5">
          <span className="text-xs text-gray-500 font-medium">Spacing</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-6">Top</span>
            <input
              type="range"
              min="-0.1"
              max="0.5"
              step="0.01"
              value={sectionSpacing?.marginTop ?? 0}
              onChange={(e) => onSpacingChange(card.key, { marginTop: parseFloat(e.target.value), marginBottom: sectionSpacing?.marginBottom ?? 0 })}
              className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <span className="text-xs text-gray-400 min-w-[48px] text-right">
              {(sectionSpacing?.marginTop ?? 0).toFixed(2)}in
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-6">Bot</span>
            <input
              type="range"
              min="-0.1"
              max="0.5"
              step="0.01"
              value={sectionSpacing?.marginBottom ?? 0}
              onChange={(e) => onSpacingChange(card.key, { marginTop: sectionSpacing?.marginTop ?? 0, marginBottom: parseFloat(e.target.value) })}
              className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <span className="text-xs text-gray-400 min-w-[48px] text-right">
              {(sectionSpacing?.marginBottom ?? 0).toFixed(2)}in
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default SectionCardComponent
